import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, Pencil, Play, RotateCcw, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { DeckFormDialog } from "@/components/flashcards/DeckFormDialog"
import { AttemptList } from "@/components/quizzes/AttemptList"
import { QuizEditor } from "@/components/quizzes/QuizEditor"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { AttemptSummary, InProgressAttempt, QuizDetail, QuizSummary } from "@/types/quizzes"

export default function QuizShow({
  quiz,
  in_progress,
  recent_attempts,
  content_area_groups,
}: {
  quiz: QuizSummary | QuizDetail
  in_progress: InProgressAttempt | null
  recent_attempts: AttemptSummary[]
  content_area_groups: ContentAreaGroup[]
}) {
  const intl = useIntl()
  const format = useFormat()
  const [editing, setEditing] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const basePath = `/quizzes/${quiz.id}`
  const summary = intl.formatMessage({ id: "quizzes.show_meta" }, { title: quiz.title, questions: format.questionCount(quiz.question_count) })
  const start = () => router.post(`${basePath}/attempts`)

  return (
    <>
      <Head title={quiz.title}>
        <meta name="description" content={quiz.description || summary} />
        <meta property="og:title" content={quiz.title} />
        <meta property="og:description" content={quiz.description || summary} />
      </Head>
      <AppShell>
        <Link href="/quizzes" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.title" })}
        </Link>
        <PageHeader
          title={quiz.title}
          description={
            <>
              {quiz.official && (
                <Badge tone="accent" className="mr-2">
                  {intl.formatMessage({ id: "flashcards.official" })}
                </Badge>
              )}
              {format.questionCount(quiz.question_count)} · {format.bestScore(quiz.best_score)}
              {quiz.description ? ` · ${quiz.description}` : ""}
              {!quiz.official && quiz.source && (
                <>
                  {` · ${intl.formatMessage({ id: "quizzes.made_from" })} `}
                  <Link href={`/sources/${quiz.source.id}`}>{quiz.source.title}</Link>
                </>
              )}
            </>
          }
          actions={
            <>
              {!quiz.official && (
                <>
                  <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "quizzes.edit_quiz" })} onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "quizzes.delete_quiz" })} onClick={() => setDeleting(true)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
              {in_progress ? (
                <>
                  <Button type="button" variant="secondary" onClick={start} disabled={quiz.question_count === 0}>
                    <RotateCcw className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.start_over" })}
                  </Button>
                  <Button asChild>
                    <Link href={`/attempts/${in_progress.id}`}>
                      <Play className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.resume" })}
                    </Link>
                  </Button>
                </>
              ) : (
                quiz.question_count > 0 && (
                  <Button type="button" onClick={start}>
                    <Play className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.take_quiz" })}
                  </Button>
                )
              )}
            </>
          }
        />

        {in_progress && (
          <p className="mt-4 text-sm text-ink-muted">
            {intl.formatMessage(
              { id: in_progress.retry ? "quizzes.progress_retry" : "quizzes.progress" },
              { answered: in_progress.answered, questions: format.questionCount(in_progress.question_count) },
            )}
          </p>
        )}

        <section className="mt-8">
          <h2>{intl.formatMessage({ id: "quizzes.attempts_title" })}</h2>
          {recent_attempts.length === 0 ? (
            <p className="mt-2">
              {quiz.question_count === 0
                ? intl.formatMessage({ id: "quizzes.add_to_take" })
                : intl.formatMessage({ id: "quizzes.no_attempts" })}
            </p>
          ) : (
            <div className="mt-4">
              <AttemptList attempts={recent_attempts} />
            </div>
          )}
        </section>

        {"questions" in quiz && (
          <QuizEditor quiz={quiz} basePath={basePath} contentAreaGroups={content_area_groups} />
        )}

        {!quiz.official && (
          <>
            <DeckFormDialog
              open={editing}
              onOpenChange={setEditing}
              action={basePath}
              method="patch"
              title={intl.formatMessage({ id: "quizzes.edit_quiz" })}
              submitLabel={intl.formatMessage({ id: "quizzes.save" })}
              initial={{ title: quiz.title, description: quiz.description }}
            />
            <ConfirmDialog
              open={deleting}
              onOpenChange={setDeleting}
              title={intl.formatMessage({ id: "quizzes.delete_quiz_title" })}
              description={intl.formatMessage({ id: "quizzes.delete_quiz_body" }, { title: quiz.title, questions: format.questionCount(quiz.question_count) })}
              confirmLabel={intl.formatMessage({ id: "quizzes.delete_quiz" })}
              onConfirm={() => router.delete(basePath)}
            />
          </>
        )}
      </AppShell>
    </>
  )
}
